import { FilterItem } from "@components/assessments/filters/Filters";
import { Text } from "@core";
import { AppliedFilterInterface } from "@shopify/polaris";
import React, { useEffect, useState } from "react";

interface GroupNameFilterProps {
  selected: string[];
  setSelected: (selected: string[]) => void;
  onRemove?: () => void;
  onAdd?: () => void;
  children: Array<{
    user: {
      groupName?: string;
      parents?: Array<{
        user: {
          groupName?: string;
        };
      }>;
    };
  }>;
}

type Result = {
  filterItem: FilterItem;
  selectedGroups: string[];
  selectedCaregiverGroups: string[];
  setSelectedGroups: (selected: string[]) => void;
  setSelectedCaregiverGroups: (selected: string[]) => void;
};

export default function useGroupNameFilter({ children, onRemove, onAdd }: GroupNameFilterProps): Result {
  const [selectedGroups, setSelectedGroups] = useState(() => {
    const stored = localStorage.getItem("selectedGroups");
    return stored ? JSON.parse(stored) : [];
  });

  const [selectedCaregiverGroups, setSelectedCaregiverGroups] = useState(() => {
    const stored = localStorage.getItem("selectedCaregiverGroups");
    return stored ? JSON.parse(stored) : [];
  });

  useEffect(() => {
    localStorage.setItem("selectedGroups", JSON.stringify(selectedGroups));
  }, [selectedGroups]);

  useEffect(() => {
    localStorage.setItem("selectedCaregiverGroups", JSON.stringify(selectedCaregiverGroups));
  }, [selectedCaregiverGroups]);

  const childGroupNames = React.useMemo(() => {
    const groups = new Set<string>();
    children?.forEach(child => {
      if (child.user.groupName) {
        groups.add(child.user.groupName);
      }
    });
    selectedGroups?.forEach(group => {
      if (!groups.has(group)) {
        groups.add(group);
      }
    });

    return Array.from(groups).sort();
  }, [children, selectedGroups]);

  const caregiverGroupNames = React.useMemo(() => {
    const groups = new Set<string>();
    children?.forEach(child => {
      child.user.parents?.forEach(parent => {
        if (parent.user.groupName) {
          groups.add(parent.user.groupName);
        }
      });
    });
    selectedCaregiverGroups?.forEach(group => {
      if (!groups.has(group)) {
        groups.add(group);
      }
    });

    return Array.from(groups).sort();
  }, [children, selectedCaregiverGroups]);

  const appliedFilter: AppliedFilterInterface | null = React.useMemo(() => {
    const filteredGroupCount = selectedGroups.length + selectedCaregiverGroups.length;
    return filteredGroupCount > 0
      ? {
          key: "groupName",
          label: `Filtering ${filteredGroupCount} ${filteredGroupCount === 1 ? "Group" : "Groups"}`,
          onRemove: () => {
            setSelectedGroups([]);
            setSelectedCaregiverGroups([]);
          }
        }
      : null;
  }, [selectedGroups, selectedCaregiverGroups]);

  return {
    filterItem: {
      key: "groupName",
      label: "Group",
      onAction: () => {
        if (onAdd) {
          onAdd();
        }
      },
      filter: (
        <div style={{ padding: "8px", maxHeight: "300px" }}>
          <CheckBoxGroup
            label="Child Group"
            groupNames={childGroupNames}
            selected={selectedGroups}
            setSelected={setSelectedGroups}
          />
          <CheckBoxGroup
            label="Caregiver Group"
            groupNames={caregiverGroupNames}
            selected={selectedCaregiverGroups}
            setSelected={setSelectedCaregiverGroups}
          />
        </div>
      ),
      appliedFilter,
      onRemove: () => {
        if (onRemove) {
          onRemove();
        }
        setSelectedGroups([]);
        setSelectedCaregiverGroups([]);
      },
      clearAll: () => {
        setSelectedGroups([]);
        setSelectedCaregiverGroups([]);
      }
    },
    selectedGroups,
    selectedCaregiverGroups,
    setSelectedGroups,
    setSelectedCaregiverGroups
  };
}

function CheckBoxGroup({
  label,
  groupNames,
  selected,
  setSelected
}: {
  label: string;
  groupNames: string[];
  selected: string[];
  setSelected: (selected: string[]) => void;
}) {
  return (
    <>
      <Text style={{ fontWeight: "bold" }}>{label}</Text>
      <div style={{ padding: "8px", maxHeight: "300px", overflowY: "auto" }}>
        {groupNames.map(groupName => (
          <label
            key={groupName}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "4px 0",
              cursor: "pointer"
            }}
          >
            <input
              type="checkbox"
              checked={selected.includes(groupName)}
              onChange={e => {
                if (e.target.checked) {
                  setSelected([...selected, groupName]);
                } else {
                  setSelected(selected.filter(name => name !== groupName));
                }
              }}
            />
            {groupName}
          </label>
        ))}
      </div>
    </>
  );
}
